import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { Mail, MapPin, Phone, User, ChevronLeft, Users, School, Calendar, IdCard } from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

const ParentProfile = () => {
  const { parentId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [parentData, setParentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation('parentprofile');

  useEffect(() => {
    const fetchParentData = async () => {
      try {
        const response = await api.get(`/api/v1/admin/schools/${user.school_reg}/parents/${parentId}`);
        setParentData(response.data);
      } catch (error) {
        setError(error.message);
        console.error('Error fetching parent data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchParentData();
  }, [parentId, api, user.school_reg]);

  const getRandomColor = (name) => {
    const colors = [
      'bg-blue-600', 'bg-purple-600', 'bg-pink-600', 'bg-red-600', 
      'bg-emerald-600', 'bg-yellow-600', 'bg-cyan-600', 'bg-indigo-600', 
      'bg-rose-600', 'bg-green-600'
    ];
    const index = name.split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500 text-lg">{t('error')}: {error}</div>
      </div>
    );
  }

  if (!parentData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500 text-lg">{t('parentNotFound')}</div>
      </div>
    );
  }

  const { parent, students } = parentData;

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>{t('back')}</span>
        </button>
        
        <button
          onClick={() => navigate(`/edit-parent/${parentId}`)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t('editProfile')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Parent Basic Info Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-2xl font-semibold text-gray-800">{t('parentProfile')}</h2>
          </div>
          <div className="p-6">
            <div className="flex flex-col items-center mb-6">
              <div className={`w-32 h-32 rounded-full flex items-center justify-center text-3xl text-white ${getRandomColor(parent.name)}`}>
                {parent.name.charAt(0)}
              </div>
              <h2 className="text-2xl font-bold mt-4 text-gray-800">{parent.name}</h2>
              <p className="text-gray-600">{parent.id_number}</p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-2xl font-semibold text-gray-800">{t('contactInformation')}</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoItem icon={Mail} label={t('email')} value={parent.email} />
              <InfoItem icon={Phone} label={t('phone')} value={parent.phone} />
              <InfoItem icon={MapPin} label={t('address')} value={parent.address} />
              <InfoItem icon={User} label={t('relationType')} value={parent.relation_type} />
            </div>
          </div>
        </div>

        {/* Students Information Card - Full Width */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden lg:col-span-2">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-2xl font-semibold text-gray-800">{t('associatedStudents')}</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {students.map(student => (
                <div 
                  key={student.id}
                  className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={() => navigate(`/student-profile/${student.id}`)}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl text-white ${getRandomColor(student.name)}`}>
                      {student.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{student.name}</h3>
                      <p className="text-sm text-gray-600">{t('admission')}: {student.admission_number}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <InfoItem icon={School} label={t('class')} value={student.class.name} />
                    <InfoItem icon={Users} label={t('stream')} value={student.stream.name} />
                    <InfoItem icon={Calendar} label={t('dateOfBirth')} value={format(new Date(student.date_of_birth), 'dd MMM yyyy')} />
                    <InfoItem icon={User} label={t('gender')} value={student.gender} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper component for info items
const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-5 h-5 text-gray-500 mt-1" />
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-base font-medium text-gray-800">{value || 'N/A'}</p>
    </div>
  </div>
);

export default ParentProfile;